import { useEffect, useState } from 'react'
import { Trash2, Upload, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type { WatchRule, WatchRuleInput, WatchRuleKind } from '@/types/airTraffic'
import { AIR, FONT_MONO } from './theme'
import { ActionButton, Empty, SectionLabel, errorText } from './ui'

const KINDS: Array<{ id: WatchRuleKind; label: string; placeholder: string; help: string }> = [
  { id: 'callsign_prefix', label: 'Callsign prefix', placeholder: 'RCH, FORTE, NATO', help: 'Any callsign starting with these letters' },
  { id: 'hex', label: 'ICAO hex', placeholder: 'ae1234', help: 'One specific airframe' },
  { id: 'registration', label: 'Registration', placeholder: 'N123AB, A6-EUA', help: 'One specific airframe by tail number' },
  { id: 'type', label: 'Aircraft type', placeholder: 'C17, K35R, RC135', help: 'ICAO type designator' },
  { id: 'operator', label: 'Operator', placeholder: 'United States Air Force', help: 'Registered owner/operator contains this text' },
  { id: 'airport', label: 'Airport', placeholder: 'OKKK or KWI', help: 'Military, privacy-listed or emergency aircraft within 40 km' },
  { id: 'area', label: 'Current view area', placeholder: 'Name for this area', help: 'Military, privacy-listed or emergency aircraft entering a circle around the view centre' },
]

interface Props {
  open: boolean
  onClose: () => void
  isAdmin: boolean
  viewCircle: { lat: number; lon: number; radius_km: number } | null
  onChanged: () => void
}

export default function WatchRulesDialog({ open, onClose, isAdmin, viewCircle, onChanged }: Props) {
  const [rules, setRules] = useState<WatchRule[] | null>(null)
  const [kind, setKind] = useState<WatchRuleKind>('callsign_prefix')
  const [value, setValue] = useState('')
  const [email, setEmail] = useState(false)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const load = () =>
    apiClient
      .airWatchRules()
      .then((r) => setRules(r.rules))
      .catch(() => setRules([]))

  useEffect(() => {
    if (open) load()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  const spec = KINDS.find((k) => k.id === kind)!

  const create = async () => {
    setFormError(null)
    const body: WatchRuleInput = { kind, value: value.trim(), notify: email ? ['in_app', 'email'] : ['in_app'] }
    if (kind === 'area') {
      if (!viewCircle) return setFormError('Pan the map to the area first.')
      Object.assign(body, viewCircle, { match: 'notable' })
    }
    if (kind === 'airport') Object.assign(body, { radius_km: 40, match: 'notable' })
    setBusy(true)
    try {
      await apiClient.airCreateWatchRule(body)
      setValue('')
      await load()
      onChanged()
    } catch (e) {
      setFormError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const patch = async (r: WatchRule, p: Partial<WatchRuleInput>) => {
    try {
      await apiClient.airUpdateWatchRule(r.id, p)
      await load()
      onChanged()
    } catch (e) {
      toast({ title: 'Couldn’t update rule', description: errorText(e), variant: 'destructive' })
    }
  }

  const remove = async (r: WatchRule) => {
    await apiClient.airDeleteWatchRule(r.id).catch(() => null)
    await load()
    onChanged()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[8vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="air-rules-title"
        className="flex max-h-[80vh] w-full max-w-[620px] flex-col rounded-sm border shadow-2xl"
        style={{ background: AIR.panel, borderColor: AIR.lineStrong, color: AIR.text }}
      >
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: AIR.line }}>
          <div>
            <h2 id="air-rules-title" className="text-[14px] font-semibold" style={{ color: AIR.bright }}>
              Watch rules
            </h2>
            <p className="text-[11px]" style={{ color: AIR.muted }}>
              Matches raise an alert in the ticker. Emergency squawks and military aircraft near your GEOINT AOIs alert without a rule.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-sm p-1 hover:bg-white/5">
            <X className="h-4 w-4" style={{ color: AIR.muted }} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pb-3">
          <SectionLabel>New rule</SectionLabel>
          <div className="grid grid-cols-[170px_1fr] gap-2 px-3">
            <select
              id="air-rule-kind"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as WatchRuleKind)
                setFormError(null)
              }}
              className="h-8 rounded-sm px-2 text-[12px] outline-none"
              style={{ background: AIR.bg, color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}` }}
            >
              {KINDS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
            <input
              id="air-rule-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              placeholder={spec.placeholder}
              className="h-8 rounded-sm px-2 text-[12px] outline-none placeholder:opacity-50"
              style={{ background: AIR.bg, color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}`, fontFamily: FONT_MONO }}
            />
          </div>
          <div className="flex items-center justify-between gap-3 px-3 pt-2">
            <span className="text-[11px]" style={{ color: AIR.muted }}>
              {spec.help}
              {kind === 'area' && viewCircle && ` (${viewCircle.radius_km} km around ${viewCircle.lat.toFixed(2)}, ${viewCircle.lon.toFixed(2)})`}
            </span>
            <div className="flex shrink-0 items-center gap-3">
              <label className="flex items-center gap-1.5 text-[12px]">
                <input id="air-rule-email" type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} className="h-3.5 w-3.5 accent-teal-500" />
                Email me
              </label>
              <ActionButton tone="primary" onClick={create} busy={busy} disabled={!value.trim() && kind !== 'area'}>
                Add rule
              </ActionButton>
            </div>
          </div>
          {formError && (
            <p className="px-3 pt-1.5 text-[12px]" style={{ color: AIR.emergency }}>
              {formError}
            </p>
          )}

          <SectionLabel>Your rules · {rules?.length ?? 0}</SectionLabel>
          {rules?.length === 0 && <Empty>No rules yet.</Empty>}
          {rules?.map((r) => (
            <div key={r.id} className="flex items-center gap-3 border-t px-3 py-2" style={{ borderColor: AIR.line }}>
              <input
                id={`air-rule-on-${r.id}`}
                type="checkbox"
                checked={r.enabled !== false}
                onChange={(e) => patch(r, { enabled: e.target.checked })}
                title="Enabled"
                className="h-3.5 w-3.5 accent-teal-500"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px]" style={{ color: r.enabled === false ? AIR.muted : AIR.bright }}>
                  {r.label}
                </div>
                <div className="truncate text-[11px]" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                  {KINDS.find((k) => k.id === r.kind)?.label} · {r.value}
                  {r.radius_km ? ` · ${r.radius_km} km` : ''}
                </div>
              </div>
              <label className="flex shrink-0 items-center gap-1 text-[11px]" style={{ color: AIR.muted }}>
                <input
                  id={`air-rule-mail-${r.id}`}
                  type="checkbox"
                  checked={(r.notify || []).includes('email')}
                  onChange={(e) => patch(r, { notify: e.target.checked ? ['in_app', 'email'] : ['in_app'] })}
                  className="h-3.5 w-3.5 accent-teal-500"
                />
                Email
              </label>
              <button type="button" onClick={() => remove(r)} aria-label={`Delete ${r.label}`} className="rounded-sm p-1 hover:bg-white/5">
                <Trash2 className="h-3.5 w-3.5" style={{ color: AIR.muted }} />
              </button>
            </div>
          ))}

          {isAdmin && <LiveAtcImport />}
        </div>
      </div>
    </div>
  )
}

function LiveAtcImport() {
  const [files, setFiles] = useState<File[]>([])
  const [lines, setLines] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const run = async () => {
    setBusy(true)
    setResult(null)
    try {
      const r = await apiClient.airImportLiveAtc(files, lines)
      setResult(`${r.parsed} feeds read: ${r.added} added, ${r.updated} updated.`)
      setFiles([])
      setLines('')
    } catch (e) {
      setResult(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <SectionLabel>Admin · LiveATC feed catalogue</SectionLabel>
      <div className="space-y-2 px-3 text-[11px]" style={{ color: AIR.muted }}>
        <p>
          LiveATC blocks our server, so feed names are imported by hand. Open liveatc.net/search/?icao=OKKK (or a regional feed list) in your
          browser, save the page (Ctrl+S) and upload it here, or paste lines like <span style={{ fontFamily: FONT_MONO, color: AIR.text }}>OKKK okbk_twr Kuwait Tower</span>.
        </p>
        <div className="flex items-center gap-2">
          <label
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm px-2.5 py-1.5"
            style={{ background: AIR.panel2, color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}` }}
          >
            <Upload className="h-3.5 w-3.5" /> Saved pages
            <input
              id="air-atc-files"
              type="file"
              accept=".html,.htm,text/html"
              multiple
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </label>
          <span>{files.length ? `${files.length} file(s) selected` : 'None selected'}</span>
        </div>
        <textarea
          id="air-atc-lines"
          value={lines}
          onChange={(e) => setLines(e.target.value)}
          rows={3}
          placeholder={'OKKK okbk_twr Kuwait Tower\nOMDB omdb_app Dubai Approach'}
          className="w-full rounded-sm px-2 py-1.5 text-[12px] outline-none placeholder:opacity-50"
          style={{ background: AIR.bg, color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}`, fontFamily: FONT_MONO }}
        />
        <div className="flex items-center gap-3">
          <ActionButton onClick={run} busy={busy} disabled={!files.length && !lines.trim()}>
            Import feeds
          </ActionButton>
          {result && <span style={{ color: AIR.text }}>{result}</span>}
        </div>
      </div>
    </>
  )
}
